import React, { useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/utils';
import { FileText, Upload, Download, AlertTriangle, Loader2, Trash2, CheckCircle2 } from 'lucide-react';

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Generic file-upload panel used by the Manufacturing Order Form and the
 * Factory Processing Sheet. Provides upload / replace / download / delete
 * with a required/uploaded status pill.
 *
 * Required props:
 *   - order:            order document
 *   - fieldKey:         key on the order doc (e.g. 'manufacturing_form')
 *   - label:            display label (e.g. 'Manufacturing Order Form')
 *   - allowedExts:      array of allowed extensions ['pdf','xls','xlsx']
 *   - acceptAttr:       string for <input accept="...">
 *   - description:      shown when file is missing
 *   - uploadFn(id,file), deleteFn(id), downloadUrlFn(id)
 *   - onChanged():      called after any mutation
 *   - blocking:         when true, shows amber "Required" styling if empty
 */
export default function OrderFilePanel({
  order,
  fieldKey,
  label,
  allowedExts,
  acceptAttr,
  description,
  uploadFn,
  deleteFn,
  downloadUrlFn,
  onChanged,
  blocking = true,
  maxMb = 25,
}) {
  const { isProduction, isAdmin } = useAuth();
  const canUpload = isProduction();
  const canDelete = isAdmin();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const file = order?.[fieldKey];
  const hasFile = !!file;

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    const ext = picked.name.split('.').pop()?.toLowerCase();
    if (!allowedExts.includes(ext)) {
      toast.error(`Only ${allowedExts.map(x => '.' + x).join(', ')} allowed`);
      return;
    }
    if (picked.size > maxMb * 1024 * 1024) {
      toast.error(`File too large (max ${maxMb} MB)`);
      return;
    }
    try {
      setUploading(true);
      await uploadFn(order.id, picked);
      toast.success(hasFile ? `${label} replaced` : `${label} uploaded`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove the ${label}? The factory will no longer see this order until a new file is uploaded.`)) return;
    try {
      setRemoving(true);
      await deleteFn(order.id);
      toast.success(`${label} removed`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove file');
    } finally {
      setRemoving(false);
    }
  };

  const handleDownload = () => window.open(downloadUrlFn(order.id), '_blank', 'noopener');

  const showBlockingStyle = blocking && !hasFile;

  return (
    <Card
      className={showBlockingStyle
        ? 'border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40'
        : (hasFile ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-500/5 dark:border-emerald-500/30' : '')}
      data-testid={`order-file-panel-${fieldKey}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              hasFile
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
            }`}>
              {hasFile ? <FileText className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</h3>
                {hasFile ? (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60 hover:bg-emerald-100" data-testid={`${fieldKey}-status-uploaded`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Uploaded
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border-amber-300/60 hover:bg-amber-100" data-testid={`${fieldKey}-status-missing`}>
                    Required
                  </Badge>
                )}
              </div>
              {hasFile ? (
                <div className="mt-1 text-[12px] text-slate-600 dark:text-slate-400 space-y-0.5">
                  <p className="truncate" data-testid={`${fieldKey}-filename`}>
                    <span className="font-medium text-slate-800 dark:text-slate-200">{file.filename || 'file'}</span>
                    {file.size ? <span className="ml-1 text-slate-500">· {humanSize(file.size)}</span> : null}
                    {file.extension ? <span className="ml-1 uppercase text-slate-500">· {file.extension}</span> : null}
                  </p>
                  <p data-testid={`${fieldKey}-uploader`}>
                    Uploaded by <span className="font-medium text-slate-800 dark:text-slate-200">{file.uploaded_by_name || 'Unknown'}</span>
                    {file.uploaded_at ? <> · <span title={file.uploaded_at}>{formatDateTime(file.uploaded_at)}</span></> : null}
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-[12px] text-amber-800 dark:text-amber-300 max-w-prose">
                  {description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {hasFile && (
              <Button size="sm" variant="outline" onClick={handleDownload} data-testid={`${fieldKey}-download-btn`}>
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            )}
            {canUpload && (
              <Button
                size="sm"
                onClick={handlePick}
                disabled={uploading}
                data-testid={`${fieldKey}-upload-btn`}
                className={hasFile ? '' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                {hasFile ? 'Replace' : 'Upload'}
              </Button>
            )}
            {hasFile && canDelete && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemove}
                disabled={removing}
                data-testid={`${fieldKey}-delete-btn`}
                className="text-rose-700 border-rose-200 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-500/40"
              >
                {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={acceptAttr}
              className="hidden"
              onChange={handleFile}
              data-testid={`${fieldKey}-file-input`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
